"use client";

import { BadgePlus, ChevronDown, Flame, History, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shadcn/ui/dropdown-menu";
import { FeedEvents, trackEvent } from "@/analytics";

import { Button } from "@/shadcn/ui/button";
import { cn } from "@/utils/cn";
import { useRouter } from "next/navigation";

export type FilterType = "recommended" | "recent" | "hot" | "mycomedians";

interface FeedFilterProps {
  isLoggedOut: boolean;
  activeFilter: FilterType;
  onFilterChange: (filter: FilterType) => void;
  showMyComediansFilter?: boolean;
}

const filterIcons = {
  recommended: <BadgePlus className="h-4 w-4" />,
  recent: <History className="h-4 w-4" />,
  hot: <Flame className="h-4 w-4" />,
  mycomedians: <Users className="h-4 w-4" />,
};

export function FeedFilter({
  isLoggedOut,
  activeFilter,
  onFilterChange,
  showMyComediansFilter = false,
}: FeedFilterProps) {
  const router = useRouter();

  const handleFilterChange = (filter: FilterType) => {
    if (isLoggedOut && (filter === "recommended" || filter === "mycomedians")) {
      trackEvent(FeedEvents.FEED_FILTER_SIGNIN_REDIRECT, {
        from_filter: activeFilter,
        to_filter: filter,
      });
      router.push(`/signin?redirectRoute=/?targetFilter=${filter}`);
      return;
    }

    trackEvent(FeedEvents.FEED_FILTER_CHANGE, {
      from_filter: activeFilter,
      to_filter: filter,
    });
    onFilterChange(filter);
  };

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) {
          trackEvent(FeedEvents.FEED_FILTER_OPEN);
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="gap-2 px-0 md:px-2 focus-visible:ring-0"
        >
          {filterIcons[activeFilter]}
          {activeFilter === "mycomedians"
            ? "My Comedians"
            : activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => handleFilterChange("recommended")}
          className={cn(
            "gap-2",
            activeFilter === "recommended" && "bg-gray-100"
          )}
        >
          <BadgePlus size={16} />
          Recommended
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleFilterChange("recent")}
          className={cn("gap-2", activeFilter === "recent" && "bg-gray-100")}
        >
          <History size={16} />
          Recent
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleFilterChange("hot")}
          className={cn("gap-2", activeFilter === "hot" && "bg-gray-100")}
        >
          <Flame size={16} />
          Hot
        </DropdownMenuItem>
        {showMyComediansFilter && (
          <DropdownMenuItem
            onClick={() => handleFilterChange("mycomedians")}
            className={cn(
              "gap-2",
              activeFilter === "mycomedians" && "bg-gray-100"
            )}
          >
            <Users size={16} />
            My Comedians
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
